import { until } from '@vueuse/core';
import { K_COLOR_OPTIONS, K_FOG_OPTIONS, K_LIGHT_OPTIONS, K_MATERIAL_OPTIONS, K_PROPS_CAMERA, K_PROPS_FOG, K_PROPS_LIGHT, K_PROPS_MATERIAL, K_PROPS_OBJECT } from 'components/Kontrol/const';
import { getEnv, kontrolErrors } from 'config';
import { find, has, isArray, isPlainObject, keys, merge, without, omit } from 'lodash';
import { computed } from 'vue';
import { K_AddOption, K_Param, K_StoreMethods, K_TWOption } from "./types";


// Param transformed
type PimpedParam = {
  values?: string | string[];
  options: K_AddOption;
}

// String or Array of String ==> To include or exclude properties (internal use)
type PropertyValues = string | string[];

/**
 * Static class to access the Kontrol store
 * only if we have the env VITE_KONTROLS = true, otherwise it's a blank tool  
 **/
export class Kontrol {

  // Store refs with only accessible methods
  private static store : K_StoreMethods;

  /**
   * Add a whole object or specific properties to the Kontrol panel
   * @param param Object of props
   * @param values String or Array of properties to include or Tweakpane and path customisation + change event
   * @param options Tweakpane and path customisation + change event
   */
  static async add(param : K_Param, values?: PropertyValues | K_AddOption, options?:K_AddOption | K_TWOption) {
    const enabled = await this._isEnabled();
    if(!enabled) return;

    //wait for kontrol ready
    await until(computed( () => this.store.ready)).toBe(true)

    // check options
    const instances = [isArray(values), isPlainObject(values)];
    
    // params transformed
    let pimpedParam : PimpedParam;
    
    // string or array
    if(!instances[1]) {
      pimpedParam = this._pimpMyParam(param, values as PropertyValues);
      
      // transform options if we have only one value (better syntax for the user)
      const hasOneValues = !instances[0] || (instances[0] && (values as string[]).length === 1);

      //we want to keep the path settings outside the options, otherwise we lose it
      let optionsWithoutPath = omit(options, ['path']);

      if(hasOneValues && !has(optionsWithoutPath, values as string)) {
        optionsWithoutPath = {[values as string] : optionsWithoutPath};
      }

      //merge the both options (options for the value and for the path (if necessary))
      options = {...options, ...optionsWithoutPath};

      this.store.addParam(param, pimpedParam.values, merge(pimpedParam.options, options || {}));
    }
    // K_AddOptions
    else{
      pimpedParam = this._pimpMyParam(param);
      this.store.addParam(param, pimpedParam.values, merge(pimpedParam.options, options || values || {}));
    }
  }

  /**
   * Add whole object without specific properties to the Kontrol panel
   * @param param Object of props
   * @param values String or Array of properties to exclude
   * @param options Tweakpane and path customisation + change event
   */
  static async addWithout(param: K_Param, values: string | string[], options?: K_AddOption) {
    const enabled = await this._isEnabled();
    if(!enabled) return;
    
    //wait for kontrol ready
    await until(computed( () => this.store.ready)).toBe(true);
    
    // be sure to have a dynamic arguments list
    if(!isArray(values)) values = [values as string]

    const pimpedParam = this._pimpMyParam(param, values, true);

    //add through the store
    this.store.addParam(param, pimpedParam.values, merge(pimpedParam.options, options || {}));
  }

  /**
   * Clear the whole Kontrol panel
   */
  static async clear() {
    const enabled = await this._isEnabled();
    if(!enabled) return;
    
    await until(computed( () => this.store.ready)).toBe(true)
    this.store.clear();
  }
  
  /**
   * Dispose the whole Kontrol panel (removed it from the dom)
   */
  static async dispose() {
    const enabled = await this._isEnabled();
    if(!enabled) return;
    
    await until(computed( () => this.store.ready)).toBe(true)
    this.store.dispose();
  }
  
  /**
   * For webgl plugin only (begin and start to watch fps between raf renders)
   * @param mode begin or end
   */
  static async watchFps(mode:string) {
    const enabled = await this._isEnabled();
    if(!enabled) return;

    await until(computed( () => this.store.ready)).toBe(true)
    this.store.fps(mode);
  }

  //------------------------------------ Private

  /**
   * Check if the Kontrol env is true
   */
  private static async _isEnabled() {
    const env = getEnv('VITE_KONTROLS');

    // Import the store one time only if it's enabled
    if(env && !this.store) {
      this.store = (await import("components/Kontrol/store")).store; 
    }
    else if(!env && kontrolErrors){
      throw new Error("KONTROL: you need to set VITE_KONTROLS to 'true' in your env because you forgot a Kontrol reference in your code!!")
    }
    
    return env;
  } 

  /**
   * Pre-transform webgl parameters to have popular and useful properties
   * @param param Param to be pimped
   * @param param Param values (or p) to be pimped
   * @return new Param pimped
   */
  private static _pimpMyParam(param : K_Param, values?: string | string[], withoutMode : boolean = false) : PimpedParam {
    
    let options = {};

    const exceptions = ['isObject3D', 'isMaterial', 'isLight', 'isFog', 'isCamera', 'isColor'];
    const isThree = find(exceptions, key => has(param, key));
    
    // Uniform exceptions
    if(withoutMode && !isThree) {
      values = without(keys(param), ...values as string[]);
    }
    
    // Object3D exceptions
    if(param.isObject3D && !param.isLight && !param.isCamera) {
      if(!values) {
        values = K_PROPS_OBJECT;
      } else {
        if(withoutMode) values = without(K_PROPS_OBJECT, ...values);
      }
    }

    // Material exceptions
    if(param.isMaterial) {
      if(!values){
        values = K_PROPS_MATERIAL;
      } else {
        if(withoutMode) values = without(K_PROPS_MATERIAL, ...values);
      }

      options = K_MATERIAL_OPTIONS;
    }

    // Lights exceptions
    if(param.isLight) {
      if(!values) {
        values = K_PROPS_LIGHT;
      } else {
        if(withoutMode) values = without(K_PROPS_LIGHT, ...values);
      }

      options = K_LIGHT_OPTIONS;
    }

    // Fog exceptions
    if(param.isFog) {
      if(!values) {
        values = K_PROPS_FOG;
      } else {
        if(withoutMode) values = without(K_PROPS_FOG, ...values);
      }

      options = K_FOG_OPTIONS;
    }

    // Camera exceptions    
    if(param.isCamera) {
      if(!values) {
        values = K_PROPS_CAMERA;
      } else {
        if(withoutMode) values = without(K_PROPS_CAMERA, ...values);
      }
    }

    // Color exceptions
    //uniform exceptions
    const hasUniformColor = find(keys(param), key => {
      return has(param[key], 'value') && has(param[key]?.value, 'isColor');
    });

    if(param.isColor || hasUniformColor) {
      options = K_COLOR_OPTIONS;

      if(hasUniformColor) {
        options = {[hasUniformColor] : K_COLOR_OPTIONS};
      }
    }

    return {
      values,
      options,
    }
  }
}
